import { isSolved, nextHint, nextCrossHint, placementIssue } from "./engine.js";

export const HINTS_PER_ROUND = 5;

/** A single round: domain state and commands, independent of UI and storage. */
export class GameSession {
  constructor(
    puzzle,
    { ordinal = 1, date = null, saved = null, now = Date.now } = {},
  ) {
    this.puzzle = puzzle;
    this.now = now;
    this.startedAt = null;
    this.lastMarkIndex = null;
    this.markStroke = null;
    this.data = {
      puzzleId: puzzle.id,
      difficulty: puzzle.difficulty,
      variant: puzzle.variant ?? 0,
      ordinal,
      date,
      cats: [...puzzle.givens],
      marks: [],
      failedCells: [],
      lives: 3,
      mistakes: 0,
      hints: 0,
      catHelpsUsed: 0,
      elapsedMs: 0,
      started: false,
      status: "playing",
      history: [],
    };
    if (saved) this.restore(saved);
  }

  restore(saved) {
    const indices = (values) =>
      Array.isArray(values) &&
      new Set(values).size === values.length &&
      values.every(
        (index) =>
          Number.isInteger(index) &&
          index >= 0 &&
          index < this.puzzle.size ** 2,
      );
    const validCats = (values) =>
      indices(values) &&
      values.every((index) => this.puzzle.solution.includes(index)) &&
      this.puzzle.givens.every((index) => values.includes(index));
    if (
      !validCats(saved.cats) ||
      !indices(saved.marks) ||
      !Number.isInteger(saved.lives) ||
      saved.lives < 0 ||
      saved.lives > 3
    )
      throw new Error("Invalid saved round.");
    const failedCells = Array.isArray(saved.failedCells)
      ? [...new Set(saved.failedCells)].filter((index) =>
          Number.isInteger(index) && index >= 0 && index < this.puzzle.size ** 2 &&
          !this.puzzle.solution.includes(index),
        )
      : [];
    this.data = {
      ...this.data,
      cats: [...saved.cats],
      marks: saved.marks.filter((index) => !saved.cats.includes(index) && !failedCells.includes(index)),
      failedCells,
      ordinal:
        Number.isInteger(saved.ordinal) && saved.ordinal > 0
          ? saved.ordinal
          : 1,
      date: typeof saved.date === "string" ? saved.date : null,
      lives: saved.lives,
      mistakes:
        Number.isInteger(saved.mistakes) && saved.mistakes >= 0
          ? saved.mistakes
          : 3 - saved.lives,
      hints:
        Number.isInteger(saved.hints) && saved.hints >= 0 ? saved.hints : 0,
      catHelpsUsed:
        Number.isSafeInteger(saved.catHelpsUsed) && saved.catHelpsUsed >= 0
          ? saved.catHelpsUsed
          : 0,
      elapsedMs:
        Number.isFinite(saved.elapsedMs) && saved.elapsedMs >= 0
          ? Math.min(saved.elapsedMs, 604800000)
          : 0,
      started: Boolean(saved.started),
      status: isSolved(this.puzzle, saved.cats)
        ? "won"
        : saved.lives === 0
          ? "lost"
          : "playing",
      history: Array.isArray(saved.history)
        ? saved.history
            .filter((item) => validCats(item.cats) && indices(item.marks))
            .slice(-80)
            .map((item) => ({ cats: [...item.cats], marks: [...item.marks] }))
        : [],
    };
  }

  elapsed() {
    return (
      this.data.elapsedMs +
      (this.startedAt === null ? 0 : Math.max(0, this.now() - this.startedAt))
    );
  }
  resume() {
    if (
      this.data.started &&
      this.data.status === "playing" &&
      this.startedAt === null
    )
      this.startedAt = this.now();
  }
  pause() {
    this.data.elapsedMs = this.elapsed();
    this.startedAt = null;
  }
  touch() {
    this.data.started = true;
    this.resume();
  }
  remember(stroke = null) {
    this.markStroke = stroke;
    this.data.history.push({
      cats: [...this.data.cats],
      marks: [...this.data.marks],
    });
    if (this.data.history.length > 80) this.data.history.shift();
  }
  canChange(index) {
    return (
      this.data.status === "playing" &&
      Number.isInteger(index) &&
      index >= 0 &&
      index < this.puzzle.size ** 2 &&
      !this.data.failedCells.includes(index)
    );
  }
  fixed(index) {
    return this.puzzle.givens.includes(index);
  }
  complete(effect) {
    if (isSolved(this.puzzle, this.data.cats)) {
      this.data.status = "won";
      this.pause();
      return { ...effect, won: true };
    }
    return effect;
  }

  place(index) {
    this.lastMarkIndex = null;
    this.markStroke = null;
    if (!this.canChange(index) || this.fixed(index)) return {};
    if (this.data.cats.includes(index)) return this.clear(index);
    this.touch();
    const issue = placementIssue(this.puzzle, this.data.cats, index);
    if (issue) {
      this.data.failedCells.push(index);
      this.data.marks = this.data.marks.filter((mark) => mark !== index);
      this.data.lives -= 1;
      this.data.mistakes += 1;
      if (!this.data.lives) {
        this.data.status = "lost";
        this.pause();
      }
      return {
        changed: true,
        errorIndex: index,
        message: issue,
        lost: this.data.status === "lost",
      };
    }
    this.remember();
    this.data.cats.push(index);
    this.data.marks = this.data.marks.filter((mark) => mark !== index);
    return this.complete({
      changed: true,
      placementIndex: index,
      message: "Cat placed.",
    });
  }

  mark(index) {
    if (!this.canChange(index) || this.fixed(index)) return {};
    if (this.data.cats.includes(index)) return this.clear(index);
    this.touch();
    this.remember();
    this.data.marks = this.data.marks.includes(index)
      ? this.data.marks.filter((mark) => mark !== index)
      : [...this.data.marks, index];
    this.lastMarkIndex = index;
    return {
      changed: true,
      markIndices: this.data.marks.includes(index) ? [index] : [],
      message: "Note updated.",
    };
  }

  paintMarks({ indices, marked, stroke } = {}) {
    this.lastMarkIndex = null;
    if (!Array.isArray(indices) || typeof marked !== "boolean" || !stroke)
      return {};
    const changed = [...new Set(indices)].filter(
      (index) =>
        this.canChange(index) &&
        !this.fixed(index) &&
        !this.data.cats.includes(index) &&
        this.data.marks.includes(index) !== marked,
    );
    if (!changed.length) return {};
    this.touch();
    if (this.markStroke !== stroke) this.remember(stroke);
    const marks = new Set(this.data.marks);
    for (const index of changed) {
      if (marked) marks.add(index);
      else marks.delete(index);
    }
    this.data.marks = [...marks];
    return {
      changed: true,
      markIndices: marked ? changed : [],
      message: marked ? "Notes marked." : "Notes erased.",
    };
  }

  promoteMark(index) {
    const previous = this.data.history.at(-1);
    if (this.lastMarkIndex !== index || !previous || !this.canChange(index))
      return {};
    // The first click's temporary note and the second click's cat are one edit.
    this.data.history.pop();
    this.data.cats = [...previous.cats];
    this.data.marks = previous.marks.filter((mark) => !this.data.failedCells.includes(mark));
    this.lastMarkIndex = null;
    return this.place(index);
  }

  clear(index) {
    this.lastMarkIndex = null;
    this.markStroke = null;
    if (
      !this.canChange(index) ||
      this.fixed(index) ||
      (!this.data.cats.includes(index) && !this.data.marks.includes(index))
    )
      return {};
    this.touch();
    this.remember();
    this.data.cats = this.data.cats.filter((cat) => cat !== index);
    this.data.marks = this.data.marks.filter((mark) => mark !== index);
    return { changed: true, message: "Cell cleared." };
  }

  hint({ autoMark = false, limit = HINTS_PER_ROUND } = {}) {
    this.lastMarkIndex = null;
    this.markStroke = null;
    if (this.data.status !== "playing" || this.data.hints >= limit)
      return {};
    const hint = nextCrossHint(this.puzzle, this.data.cats, [...this.data.marks, ...this.data.failedCells], {
      skipBlocked: autoMark,
    });
    if (!hint) return {};
    this.touch();
    this.remember();
    this.data.hints += 1;
    this.data.marks.push(...hint.indices);
    return {
      changed: true,
      hintIndex: hint.indices[0],
      hintIndices: hint.indices,
      markIndices: hint.indices,
      message: hint.message,
    };
  }

  revealCat() {
    this.lastMarkIndex = null;
    this.markStroke = null;
    if (this.data.status !== "playing") return {};
    const hint = nextHint(this.puzzle, this.data.cats, []);
    if (!hint) return {};
    this.touch();
    this.remember();
    this.data.catHelpsUsed += 1;
    this.data.cats.push(hint.index);
    this.data.marks = this.data.marks.filter((index) => index !== hint.index);
    return this.complete({
      changed: true,
      hintIndex: hint.index,
      placementIndex: hint.index,
      message: "A cat has found its home. One cat help used.",
    });
  }

  undo() {
    this.lastMarkIndex = null;
    this.markStroke = null;
    if (this.data.status !== "playing" || !this.data.history.length) return {};
    const previous = this.data.history.pop();
    this.data.cats = previous.cats;
    this.data.marks = previous.marks.filter((mark) => !this.data.failedCells.includes(mark));
    return { changed: true, message: "Last move undone." };
  }

  snapshot() {
    const { history, ...visible } = this.data;
    return {
      ...visible,
      cats: [...this.data.cats],
      marks: [...this.data.marks],
      failedCells: [...this.data.failedCells],
      elapsedMs: this.elapsed(),
      puzzle: this.puzzle,
      score: Math.max(
        0,
        (this.data.cats.length - this.puzzle.givens.length) * 100 -
          this.data.mistakes * 25 -
          (this.data.hints + this.data.catHelpsUsed) * 25,
      ),
      canUndo: this.data.status === "playing" && this.data.history.length > 0,
    };
  }
  serialize() {
    return {
      ...structuredClone(this.data),
      elapsedMs: this.elapsed(),
      startedAt: null,
    };
  }
}
